import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * Opening the accounts that should already exist.
 *
 * A business is given an account in its director's name the moment that
 * director's BVN is confirmed. The ones that verified before that shipped got
 * nothing, and nothing in the normal course of using the app will ever open
 * one for them — the trigger is a verification they already completed.
 *
 * This replaces `business-wallets`. That page watched a pooled account
 * holding money for businesses without one of their own; there is no pool any
 * more, and no business without an account that a verified director could not
 * fix. What is left is the fixing.
 */

/** What the backfill did, or would do, to one business. */
export interface BackfillOutcome {
  businessId: string;
  businessName: string;
  /**
   * `provisioned` — an account was opened.
   * `skipped` — a rule refused it (not the first business this person
   *   created, or the provider declined). A decision, not an error.
   * `failed` — it went wrong and needs somebody.
   */
  result: 'provisioned' | 'skipped' | 'failed';
  accountNumber?: string;
  reason?: string;
}

export interface BackfillReport {
  considered: number;
  provisioned: number;
  skipped: number;
  failed: number;
  outcomes: BackfillOutcome[];
}

/** One repair, as the backend describes itself. */
export interface LedgerRepairView {
  /** Stable id. Used to run this one on its own. */
  key: string;
  label: string;
  /** What it fixes and why the rows are missing. Written for whoever is
   *  deciding whether to press the button. */
  description: string;
  /** Rows still wrong. */
  outstanding: number;
  /** Rows this run put right. Absent on a preview. */
  repaired?: number;
}

/**
 * What the ledger is still missing.
 *
 * Deliberately a list rather than named fields: the backend owns which
 * repairs exist, and a new one should appear here without this file or the
 * page changing. The migrations do the same work on deploy — these counts
 * are how you tell "it ran and matched nothing" from "it never ran".
 */
export interface LedgerRepairReport {
  repairs: LedgerRepairView[];
  /** Every repair's outstanding count, added up. */
  outstanding: number;
  /** Rows written. Zero on a preview. */
  repaired: number;
}

export const businessAccountsApi = {
  /** What the backfill would do, without opening anything. */
  previewBackfill: async () => {
    const { data } = await apiClient.get<ApiResponse<BackfillReport>>(
      '/admin/business-accounts/backfill/preview',
    );
    return data.data;
  },

  /**
   * Open the accounts.
   *
   * Safe to run more than once — a business that already holds one is
   * skipped rather than re-provisioned.
   */
  runBackfill: async () => {
    const { data } = await apiClient.post<ApiResponse<BackfillReport>>(
      '/admin/business-accounts/backfill',
    );
    return data.data;
  },

  /** What the ledger is missing, without writing anything. */
  previewLedger: async () => {
    const { data } = await apiClient.get<ApiResponse<LedgerRepairReport>>(
      '/admin/business-accounts/ledger/preview',
    );
    return data.data;
  },

  /**
   * Write the missing rows.
   *
   * Safe to call repeatedly — every insert is guarded on the row not already
   * existing, and every update is scoped to rows still in the wrong state.
   */
  repairLedger: async (key?: string) => {
    const { data } = await apiClient.post<ApiResponse<LedgerRepairReport>>(
      '/admin/business-accounts/ledger/repair',
      undefined,
      key ? { params: { key } } : undefined,
    );
    return data.data;
  },
};
